import React, { useEffect, useState, memo } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import TableSelectField from "../../../Components/Common/TableSelectField";
import FileUpload from "./FileUpload";
import DeleteRowDialog from "../../../Components/Common/DeleteRowDialog";

const DynamicTable = memo(
  ({
    fields,
    rows,
    formData,
    onRowChange,
    onAddRow,
    onRemoveRow,
    confirmRemoveRow,
    cancelRemoveRow,
    handleClose,
    handleFileUpload,
    fieldName,
    openDialog,
    errors,
  }) => {
    const [isFilled, setIsFilled] = useState("");

    useEffect(() => {
      checkIfAdded();
    }, [rows, fields]); // Run only when tables or inputData change

    const checkIfAdded = () => {
      const lastRow = rows.length > 0 ? rows[rows.length - 1] : {}; // Ensure lastRow is an object
      const filteredLastRow = Object.fromEntries(
        Object.entries(lastRow).filter(
          ([key]) => key !== "_id" && key !== "remarks" && key !== "documents",
        ),
      );
      // Check if lastRow is fully filled
      const isComplete =
        Object.keys(lastRow).length === (fields?.length || 0) &&
        Object.values(filteredLastRow)?.every(
          (val) =>
            val !== undefined &&
            val !== "" &&
            val !== null &&
            val?.length !== 0,
        );

      setIsFilled(!isComplete);
    };

    return (
      <>
        <DeleteRowDialog
          {...{ openDialog, cancelRemoveRow, confirmRemoveRow }}
        />
        {/* <Box sx={{display:"flex"}}> */}
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
            borderRadius: "10px",
            width: "100%",
          }}
        >
          <Table
            sx={{
              border: "1px solid rgba(224, 224, 224, 1)",
              borderRadius: "10px",
              width: "100%",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#488257",
                  borderRadius: "10px",
                  "& > th": {
                    color: "white",
                    fontWeight: "bold",
                    py: "0.5rem",
                    px: "0.5rem",
                    border: "1px solid rgba(224, 224, 224, 1)",
                  },
                }}
              >
                {fields?.map((field, idx) => (
                  <TableCell key={idx} sx={{ minWidth: "30px" }}>
                    {field.title}
                  </TableCell>
                ))}
                <TableCell sx={{ minWidth: "30px" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows?.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "& > td": {
                      border: "1px solid rgba(224, 224, 224, 1)",
                      py: "0rem",
                      px: "0.5rem",
                    },
                  }}
                >
                  {fields?.map((field, idx) => {
                    const selectedValues = rows
                      .map((r) => r[field.fieldName])
                      .filter(
                        (value, i) =>
                          i !== index && value !== undefined && value !== null,
                      );

                    // Filter the options to exclude already selected values
                    const availableOptions =
                      idx === 1
                        ? field.options?.filter(
                            (option) => !selectedValues.includes(option),
                          )
                        : field.options;
                    const errorMessage =
                      errors &&
                      errors[index] &&
                      errors[index][field.fieldName] &&
                      errors[index][field.fieldName];
                    const errorField = field.fieldName;

                    return (
                      <TableCell
                        key={idx}
                        sx={{
                          minWidth: field.width,
                          py: "0rem",
                          maxWidth: field.width,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          textAlign: field.fieldName === "serialNo" && "center",
                        }}
                      >
                        <Box sx={{ minHeight: "30px", maxHeight: "30px" }}>
                          {field.type === "select" ? (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0,
                              }}
                            >
                              <TableSelectField
                                value={row[field.fieldName]}
                                options={availableOptions}
                                errors={errorMessage}
                                handleChange={(e) =>
                                  onRowChange(
                                    fieldName,
                                    index,
                                    field.fieldName,
                                    e.target.value,
                                  )
                                }
                                fullWidth
                                variant="standard"
                              />
                            </Box>
                          ) : field.type === "file" ? (
                            <Box sx={{ width: "" }}>
                              <FileUpload
                                fieldName={fieldName}
                                index={index}
                                handleChange={onRowChange}
                                filesList={row[field.fieldName] || []}
                              />
                            </Box>
                          ) : (
                            <Box>
                              <TextField
                                value={row[field.fieldName]}
                                onChange={(e) =>
                                  onRowChange(
                                    fieldName,
                                    index,
                                    field.fieldName,
                                    e.target.value,
                                  )
                                }
                                placeholder={field.placeholder}
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                fullWidth
                              />
                            </Box>
                          )}
                          {errorField === field.fieldName && (
                            <Typography
                              sx={{ fontSize: "12px", lineHeight: 1 }}
                              color="#D34040"
                            >
                              {errorMessage}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                  <TableCell
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    <IconButton
                      disabled={rows?.length === 1}
                      sx={{ mt: 1, mb: 1 }}
                      onClick={() => onRemoveRow(fieldName, index)} // Pass the correct row index
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {/* </Box> */}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            onClick={onAddRow}
            disabled={isFilled}
            variant="outlined"
            sx={{
              color: "#488257",
              borderColor: "#488257",
              textTransform: "none",
              marginBottom: 3,
              borderWidth: "1.5px", // Set initial border width
              "&:hover": {
                color: "#488257", // Maintain the same color on hover
                borderColor: "#488257", // Maintain the same border color on hover
                borderWidth: "1.5px", // Make the border bolder on hover
                backgroundColor: "transparent", // Prevent background color change on hover
              },
            }}
          >
            + Add Field
          </Button>
        </Box>
      </>
    );
  },
);

export default DynamicTable;
